// =============================================================================
// useNotifications — central source of truth for the bell + /notifications.
//
// Reads from the my_notifications view (RLS-scoped to the current user
// + their role) and exposes:
//   - everything I can see
//   - just the unread + non-dismissed (for the bell badge)
//   - mutators to mark-read / dismiss / mark-all-read
// =============================================================================

import { useMemo } from 'react'
import { POLL } from '@/lib/pollTiers'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

export type NotificationSeverity = 'CRITICAL' | 'WARNING' | 'INFO'
// Every category `notification_kinds.category` may hold, in the order screens
// show them. ⚠️ ADD A NEW ONE HERE, NOT IN A SCREEN'S OWN LIST. A category the
// database emits but a screen's array omits is dropped SILENTLY — that is how
// the five SECURITY kinds shipped to prod invisible on the preferences page,
// leaving nobody able to mute or route a security alert. Both screens now
// derive from this, and the label maps are exhaustive Records, so the next
// category cannot go missing without failing tsc.
export const NOTIFICATION_CATEGORIES = [
  'COMPLIANCE', 'PRODUCTION', 'FARM', 'QC', 'STOCK', 'DISPATCH', 'PEOPLE', 'SECURITY', 'GENERAL',
] as const
export type NotificationCategory = typeof NOTIFICATION_CATEGORIES[number]

export interface MyNotification {
  notification_id: string
  kind:            string
  category:        NotificationCategory | null
  severity:        NotificationSeverity
  title:           string
  body:            string | null
  source_type:     string | null
  source_id:       string | null
  link_path:       string | null
  target_user_id:  string | null
  target_role:     string | null
  created_at:      string
  expires_at:      string | null
  read_at:         string | null
  dismissed_at:    string | null
}

export function useNotifications() {
  const qc = useQueryClient()

  const list = useQuery({
    queryKey: ['notifications'],
    refetchInterval: POLL.standard,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('my_notifications')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(200)
      if (error) throw error
      return data as MyNotification[]
    },
  })

  const unread = useMemo(
    () => (list.data ?? []).filter(n => !n.read_at && !n.dismissed_at),
    [list.data],
  )

  const markRead = useMutation({
    mutationFn: async (notificationId: string) => {
      // getSession() not getUser() — see useCurrentUser.ts header for the
      // lock-stolen race that getUser triggers under multi-tab traffic.
      const { data: { session } } = await supabase.auth.getSession()
      const user = session?.user
      if (!user) return
      const { error } = await supabase.from('notification_reads').upsert({
        notification_id: notificationId,
        user_id:         user.id,
        read_at:         new Date().toISOString(),
      }, { onConflict: 'notification_id,user_id' })
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  })

  const dismiss = useMutation({
    mutationFn: async (notificationId: string) => {
      // getSession() not getUser() — see useCurrentUser.ts header for the
      // lock-stolen race that getUser triggers under multi-tab traffic.
      const { data: { session } } = await supabase.auth.getSession()
      const user = session?.user
      if (!user) return
      const now = new Date().toISOString()
      const { error } = await supabase.from('notification_reads').upsert({
        notification_id: notificationId,
        user_id:         user.id,
        read_at:         now,
        dismissed_at:    now,
      }, { onConflict: 'notification_id,user_id' })
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  })

  const markAllRead = useMutation({
    mutationFn: async () => {
      // getSession() not getUser() — see useCurrentUser.ts header.
      const { data: { session } } = await supabase.auth.getSession()
      const user = session?.user
      if (!user || unread.length === 0) return
      const rows = unread.map(n => ({
        notification_id: n.notification_id,
        user_id:         user.id,
        read_at:         new Date().toISOString(),
      }))
      const { error } = await supabase.from('notification_reads')
        .upsert(rows, { onConflict: 'notification_id,user_id' })
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  })

  return { list, unread, markRead, dismiss, markAllRead }
}
